// 引入 React 与常用 Hook。
import React, { useEffect, useRef, useState } from 'react';
// 引入验证码脚本地址常量。
import { ALIYUN_CAPTCHA_SCRIPT_URL } from '@/constants/captcha';
// 引入样式。
import Styles from './index.module.scss';

// Extend Window interface for Aliyun Captcha
declare global {
  interface Window {
    initAliyunCaptcha?: (config: any) => void;
    AliyunCaptchaConfig?: {
      region?: string;
      prefix?: string;
    };
  }
}

export interface AliyunCaptchaInstance {
  refresh: () => void;
}

// 组件入参类型。
interface AliyunCaptchaProps {
  sceneId: string;
  prefix: string;
  mode?: 'embed' | 'popup';
  onSuccess?: (verifyParam: string) => void;
  onFail?: (error: any) => void;
  onError?: (error: any) => void;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  className?: string;
  slideStyle?: {
    width?: number;
    height?: number;
    rem?: number;
  };
  language?: string;
  getInstance?: (instance: AliyunCaptchaInstance) => void;
}

// Global script loading promise to prevent duplicate loads
// 全局脚本加载 Promise，避免重复插入 script。
let scriptLoadPromise: Promise<void> | null = null;

// 阿里云验证码组件。
const AliyunCaptcha: React.FC<AliyunCaptchaProps> = ({
  sceneId,
  prefix,
  mode = 'embed',
  onSuccess,
  onFail,
  onError,
  width = '100%',
  height = 44,
  style,
  className,
  slideStyle,
  language = 'cn',
  getInstance,
}) => {
  // 挂载容器引用。
  const containerRef = useRef<HTMLDivElement>(null);
  // 加载状态。
  const [loading, setLoading] = useState(true);
  // Generate a unique ID for the container
  const elementId = useRef(`aliyun-captcha-${Math.random().toString(36).substring(2, 9)}`).current;

  useEffect(() => {
    // 组件挂载标记，防止异步回调越界 setState。
    let isMounted = true;
    setLoading(true);

    const initCaptcha = () => {
      if (!window.initAliyunCaptcha || !containerRef.current) return;

      try {
        const config: any = {
          showErrorTip: false,
          SceneId: sceneId,
          prefix: prefix,
          mode: mode,
          element: `#${elementId}`,
          immediate: true,
          resetOnError: true,
          language: language,
          // Callback for instance creation
          getInstance: (instance: any) => {
            if (isMounted) {
              setLoading(false);
              if (getInstance) {
                getInstance(instance);
              }
            }
          },
          // 成功回调
          success: (data: any) => {
            // data contains verify param
            if (onSuccess) {
                if (data && data.captchaVerifyParam) {
                    onSuccess(data.captchaVerifyParam);
                } else if (typeof data === 'string') {
                    onSuccess(data);
                } else {
                    // Fallback or full object
                    onSuccess(JSON.stringify(data));
                }
            }
          },
          // 失败回调
          fail: (failCode: any) => {
            if (onFail) onFail(failCode);
          },
          // 报错回调
          error: (errorCode: any) => {
            if (onError) onError(errorCode);
            if (isMounted) setLoading(false);
          }
        };

        if (slideStyle) {
            // 透传滑块样式配置。
            config.slideStyle = slideStyle;
        }

        window.initAliyunCaptcha(config);
      } catch (e) {
        console.error('Aliyun Captcha Init Error:', e);
        if (onError) onError(e);
        if (isMounted) setLoading(false);
      }
    };

    const loadScript = () => {
        // 全局方法已存在时直接复用。
        if (window.initAliyunCaptcha) {
            return Promise.resolve();
        }
        
        if (!scriptLoadPromise) {
            // 首次加载时注入 script。
            scriptLoadPromise = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = ALIYUN_CAPTCHA_SCRIPT_URL;
                script.async = true;
                script.onload = () => resolve();
                script.onerror = (e) => reject(e);
                document.body.appendChild(script);
            });
        }
        return scriptLoadPromise;
    };

    loadScript()
        .then(() => {
            if (!window.initAliyunCaptcha) {
              throw new Error('initAliyunCaptcha is not available');
            }
            if (isMounted) initCaptcha();
        })
        .catch((e) => {
            console.error('Aliyun Captcha Script Load Error:', e);
            if (onError) onError(e);
            if (isMounted) setLoading(false);
        });

    return () => {
      // 标记卸载，阻断异步回调状态更新。
      isMounted = false;
      // Cleanup logic if available in API
    };
  }, [sceneId, prefix, mode, elementId, language, slideStyle]);

  return (
    <div 
      className={`${Styles.captchaContainer} ${className || ''}`} 
      style={{ width, height, ...style }}
    >
      <div id={elementId} ref={containerRef} className={Styles.captchaMountNode} />
      {loading && <div className={Styles.loading}>加载中...</div>}
    </div>
  );
};

// 导出组件。
export default AliyunCaptcha;
